/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import type { GiftCardHolder, Product, TableStatus } from "@lemmonhq/types";
import { api } from "@app/data/api";
import { formatPrice } from "@lemmonhq/orders";
import { LemmonModal, cn, useLocalization } from "@lemmonhq/uikit";
import { useAuth, useShop } from "../../../providers";
import {
  giftCardAmountsOf,
  isTableCreditProduct,
  tableCreditOf,
} from "@app/vue/components/TableCredit/tableCredit";
import { GiftCardAmountPicker } from "./AmountPicker";
import { GiftCardPreview } from "./CardPreview";
import { GiftCardHolderFields } from "./HolderFields";
import { GiftCardTablePicker } from "./TablePicker";

export type GiftCardSelection = {
  amount: number;
  /** Set for a table credit product, null for a plain gift card. */
  tableId: number | null;
  /** Who a plain gift card is sold to; null for table credit or when blank. */
  holder: GiftCardHolder | null;
};

export type GiftCardModalProps = {
  product: Product;
  /** Table pre-selected, e.g. the table the POS was opened from. */
  defaultTableId?: number | null;
  /**
   * Only `defaultTableId` can be chosen (top-up started from a table, e.g.
   * TableSelectModal): the table picker and the card preview are hidden.
   */
  lockTable?: boolean;
  /** Pre-filled amount, e.g. what a table is missing. */
  suggestedAmount?: number;
  onClose: () => void;
  onConfirm: (selection: GiftCardSelection) => void;
};

/**
 * Rings up a gift card or table credit: the amount, and for table credit the
 * table it is sold to (docs/plans/table_credit/pos_ui.md).
 */
export const GiftCardModal = defineComponent<GiftCardModalProps>({
  name: "GiftCardModal",
  props: [
    "product",
    "defaultTableId",
    "lockTable",
    "suggestedAmount",
    "onClose",
    "onConfirm",
  ],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const shop = useShop();
    const currency = computed(
      () => auth.user.value?.restaurant.currency || "CHF",
    );
    const isTableCredit = computed(() => isTableCreditProduct(props.product));
    const productPrice = parseFloat(props.product.price) || 0;

    // Quick amounts from the restaurant setting (Settings → Table orders).
    const presets = computed(() => {
      const amounts = giftCardAmountsOf(auth.user.value?.restaurant_settings);
      return productPrice > 0 && !amounts.includes(productPrice)
        ? [...amounts, productPrice].sort((a, b) => a - b)
        : amounts;
    });
    const tableFixed = computed(
      () => !!props.lockTable && !!props.defaultTableId,
    );
    const holder = ref<GiftCardHolder>({ name: "", email: "", phone: "" });

    const amount = ref<number | null>(
      props.suggestedAmount && props.suggestedAmount > 0
        ? Math.round(props.suggestedAmount * 100) / 100
        : productPrice > 0
          ? productPrice
          : null,
    );
    const tableId = ref<number | null>(props.defaultTableId ?? null);

    // Loaded here, with fresh credit balances: the shop's live table list can
    // still be empty right after a page load or while its poll is failing.
    const fetchedTables = ref<TableStatus[] | null>(null);
    onMounted(async () => {
      if (!isTableCredit.value) return;
      try {
        fetchedTables.value = await api.getTablesMeta();
      } catch (e) {
        console.error("Failed to load tables", e);
      }
    });
    const tables = computed(() => fetchedTables.value ?? shop.tables.value);

    const selectedTable = computed(() =>
      tables.value.find((ts) => ts.id === tableId.value),
    );
    const tableName = computed(() => selectedTable.value?.table.name ?? null);
    const currentBalance = computed(() => {
      const credit = tableCreditOf(selectedTable.value);
      return credit?.credited ? credit.balance : null;
    });

    const canConfirm = computed(
      () =>
        !!amount.value &&
        amount.value > 0 &&
        (!isTableCredit.value || !!tableId.value),
    );

    const confirmLabel = computed(() => {
      if (!amount.value) return t("general.words.add");
      const money = formatPrice(amount.value, true, 2, currency.value);
      if (isTableCredit.value && tableName.value) {
        return t("pos.gift_card.add_to_table")
          .replace("{0}", money)
          .replace("{1}", tableName.value);
      }
      return t("pos.gift_card.add_amount").replace("{0}", money);
    });

    const confirm = () => {
      if (!canConfirm.value || !amount.value) return;
      const trimmed: GiftCardHolder = {
        name: holder.value.name?.trim() || undefined,
        email: holder.value.email?.trim() || undefined,
        phone: holder.value.phone?.trim() || undefined,
      };
      props.onConfirm({
        amount: amount.value,
        tableId: isTableCredit.value ? tableId.value : null,
        holder:
          !isTableCredit.value &&
          (trimmed.name || trimmed.email || trimmed.phone)
            ? trimmed
            : null,
      });
    };

    return () => (
      <LemmonModal
        title={props.product.name}
        onClose={props.onClose}
        style="max-width: 720px; width: 100%;"
        renderButtons={() => (
          <div class="border-t bg-base-100 p-3 lg:p-4">
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="btn btn-ghost min-h-12"
                onClick={() => props.onClose()}
              >
                {t("general.words.cancel")}
              </button>
              <button
                type="button"
                class={cn(
                  "btn btn-primary min-h-12 min-w-0 flex-1 flex-nowrap gap-2 text-base",
                  canConfirm.value && "shadow-md",
                )}
                disabled={!canConfirm.value}
                onClick={confirm}
              >
                <span class="material-symbols-outlined hidden text-xl sm:inline">
                  add_shopping_cart
                </span>
                <span class="truncate">{confirmLabel.value}</span>
              </button>
            </div>
          </div>
        )}
      >
        <div class="flex flex-col gap-5 p-3 lg:p-5">
          {tableFixed.value ? null : (
            <GiftCardPreview
              productName={props.product.name}
              restaurantName={auth.user.value?.restaurant.name || ""}
              amount={amount.value}
              currency={currency.value}
              isTableCredit={isTableCredit.value}
              tableName={tableName.value}
              currentBalance={currentBalance.value}
            />
          )}
          <GiftCardAmountPicker
            value={amount.value}
            presets={presets.value}
            currency={currency.value}
            onChange={(value) => (amount.value = value)}
          />
          {isTableCredit.value ? (
            tableFixed.value ? null : (
              <GiftCardTablePicker
                tables={tables.value}
                value={tableId.value}
                currency={currency.value}
                locked={false}
                onChange={(id) => (tableId.value = id)}
              />
            )
          ) : (
            <GiftCardHolderFields
              value={holder.value}
              onChange={(value) => (holder.value = value)}
            />
          )}
        </div>
      </LemmonModal>
    );
  },
});
